/**
 * MED-781: Upcoming Sessions panel for the coach home dashboard.
 *
 * - AC25–28: same row data shown today (date, session title, time/duration,
 *   cohort+group, participant count, type tag), sorted soonest first, lookahead
 *   = next 5 (per Q4 resolution; the API already enforces this).
 * - AC5/Q5: rows are informational only — no click behavior.
 * - Visual: each row has a vertical "DD MAR" date block on the left.
 */

import { Card, Group, Stack, Text, Title, Skeleton, Box, Badge } from '@mantine/core';
import { Users, Clock } from 'react-feather';
import { EmptyState, ParticipantLabelDivergenceIndicator } from '../../../../components/common';
import type { CoachUpcomingSession } from '../../../../services/api/coachPortal';
import { composeCoachSessionLabel } from '../../utils/sessionLabel';
import { getOneOnOneTypeConfig } from '../../../../config/oneOnOneTypeConfig';
import { isCheckinOneOnOneType } from '../../../../types';

interface UpcomingSessionsPanelProps {
  sessions: CoachUpcomingSession[];
  isLoading: boolean;
}

export function UpcomingSessionsPanel({ sessions, isLoading }: UpcomingSessionsPanelProps) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="md">
        <Title order={4}>Upcoming Sessions</Title>
      </Group>

      {isLoading ? (
        <>
          {/* MED-1394: row-sized placeholders, so the page does not jump on load. */}
          <Stack gap="xs">
            {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={64} radius="sm" />)}
          </Stack>
        </>
      ) : sessions.length === 0 ? (
        <EmptyState
          size="sm"
          icon={<Clock size={24} />}
          title="No upcoming sessions"
          description="Sessions will appear once scheduled."
        />
      ) : (
        <Stack gap="xs">
          {sessions.map(session => (
            <UpcomingSessionRow key={session.id} session={session} />
          ))}
        </Stack>
      )}
    </Card>
  );
}

function UpcomingSessionRow({ session }: { session: CoachUpcomingSession }) {
  const start = new Date(session.scheduled_start);
  const day = start.getDate();
  const month = start.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
  const weekday = start.toLocaleDateString('en-US', { weekday: 'long' });
  const time = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const durationMinutes = Math.max(
    0,
    Math.round(
      (new Date(session.scheduled_end).getTime() - start.getTime()) / (60 * 1000),
    ),
  );

  // MED-1350: a check-in carries the group's id, but it is a 1:1. Name the
  // check-in and the participant, not the group and its member count.
  const isOneOnOne = session.session_type === 'one_on_one_session';
  const isCheckin = isCheckinOneOnOneType(session.one_on_one_type);
  const label = isOneOnOne
    ? [getOneOnOneTypeConfig(session.one_on_one_type).displayLabel, session.participant_name]
        .filter(Boolean)
        .join(' · ')
    : composeCoachSessionLabel({
        session_number: session.session_number,
        topic_name: session.topic_name,
        group_name: session.group_name,
        title: session.title,
      });

  return (
    <Group wrap="nowrap" align="flex-start" gap="md" py="xs">
      {/* DD MAR vertical date block */}
      <Stack gap={0} align="center" style={{ flexShrink: 0, minWidth: 36 }}>
        <Text size="lg" fw={700} lh={1}>
          {day}
        </Text>
        <Text size="xs" c="dimmed" fw={600}>
          {month}
        </Text>
      </Stack>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} lineClamp={1}>
              {label}
            </Text>
            {/* MED-1147: internal-awareness indicator — participants see a
                different label than this canonical topic. */}
            <ParticipantLabelDivergenceIndicator
              differs={session.participant_display_differs}
              participantDisplayName={session.topic_display_name}
            />
          </Group>
          <Badge
            size="xs"
            variant="light"
            color={isOneOnOne ? 'gray' : 'blue'}
            style={{ flexShrink: 0 }}
          >
            {isCheckin ? 'Check-in' : isOneOnOne ? '1:1' : 'Group'}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {weekday} · {time}
          {durationMinutes ? ` · ${durationMinutes} min` : ''}
        </Text>
        <Group gap="xs" mt={2} wrap="nowrap">
          <Text size="xs" c="dimmed" lineClamp={1}>
            {isOneOnOne ? session.cohort_name : `${session.cohort_name} · ${session.group_name}`}
          </Text>
          {!isOneOnOne && session.participant_count > 0 && (
            <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
              <Users size={11} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                {session.participant_count}
              </Text>
            </Group>
          )}
        </Group>
      </Box>
    </Group>
  );
}
